import { Badge, Popover, PopoverContent, PopoverTrigger, cn } from "@tellescope/ui";
import { Tooltip } from "@tellescope/design-system";
import { NodeViewProps, NodeViewWrapper } from "@tiptap/react";
import { useState } from "react";

import { CollectorPanel } from "../palette/CollectorPanel";
import { TokenPreview } from "../palette/TokenPreview";
import { extractTokenParams } from "../tokens/recognize";
import { getTokenLabel, isUnknownToken } from "../tokens/tokenLabel";

export const TemplateTokenNodeView = ({ node, editor, getPos }: NodeViewProps) => {
  const raw = typeof node.attrs.raw === "string" ? node.attrs.raw : "";
  const isUnknown = isUnknownToken(raw);
  const editable = extractTokenParams(raw);
  const [open, setOpen] = useState(false);

  const badge = (
    <Badge
      variant={isUnknown ? "outline" : "secondary"}
      title={raw}
      className={cn(
        "select-none font-normal",
        isUnknown && "border-dashed",
        editable ? "cursor-pointer" : "cursor-grab"
      )}
    >
      {getTokenLabel(raw)}
    </Badge>
  );

  if (!editable) {
    return (
      <NodeViewWrapper as="span" className="inline-block align-baseline" data-drag-handle draggable>
        {badge}
      </NodeViewWrapper>
    );
  }

  const replaceWith = (params: Record<string, string>) => {
    const pos = getPos();
    if (pos === undefined) return;
    const newRaw = editable.definition.build(params);
    editor
      .chain()
      .command(({ tr }) => {
        tr.setNodeAttribute(pos, "raw", newRaw);
        return true;
      })
      .run();
    setOpen(false);
  };

  // NodeViewWrapper stays draggable for reordering; the badge itself is the click-to-edit trigger.
  return (
    <NodeViewWrapper as="span" className="inline-block align-baseline" draggable>
      <Popover open={open} onOpenChange={setOpen} modal>
        <Tooltip>
          <Tooltip.Trigger asChild>
            <PopoverTrigger asChild>{badge}</PopoverTrigger>
          </Tooltip.Trigger>
          <Tooltip.Content>
            <TokenPreview raw={raw} />
          </Tooltip.Content>
        </Tooltip>
        <PopoverContent
          className="w-80 p-0"
          align="start"
          data-interactive-with-dialog
          onEscapeKeyDown={(event) => event.stopPropagation()}
        >
          <CollectorPanel
            definition={editable.definition}
            initialValues={editable.values}
            onSubmit={replaceWith}
            onClose={() => setOpen(false)}
            editing
          />
        </PopoverContent>
      </Popover>
    </NodeViewWrapper>
  );
};
